import { useEffect, useState } from 'react';
import { isDrawableLine, lineThickness, routeLinePath } from '../climbs/route-line';
import { RouteLineOverlay } from '../climbs/RouteLineOverlay';
import { climbTitle } from '../../utils/climb-title';
import type { Climb } from '../../types/climb';

interface CragSectorTopoProps {
  imageUrl: string;
  sectorName: string;
  /** The sector's climbs; the ones with a line drawn on this photo show up. */
  climbs: Climb[];
  selectedClimbId: string | null;
  onSelect: (climbId: string) => void;
  /**
   * A ceiling for the photo, as a CSS length. Set once the topo is pinned and
   * has to leave the routes room to scroll under it. The picture is fitted
   * inside the box rather than cropped, and the overlays fit the same box the
   * same way, so the lines stay on the rock at any ceiling.
   */
  imageMaxHeight?: string;
}

/**
 * The sector's photo with its whole topo on it: every route as a faint grey
 * line, and the one being read lit up.
 *
 * A crag has no wall to photograph route by route — one shot of the cliff
 * carries a dozen lines — so the picture belongs to the sector and the lines
 * are what tell the routes apart. Grey is deliberate: a dozen lines all
 * shouting cancel each other out, and the point of the picture is to answer
 * "which one is this" for one route at a time.
 *
 * The lit line is `RouteLineOverlay`, the same component the climb profile
 * draws with, but without its sweep: there the animation introduces a route you
 * have just opened, here it is the answer to "which of these is it" and has to
 * be on the rock the instant the row is tapped.
 *
 * Lines are pressable too: a climber standing under the cliff finds their route
 * by pointing at it, not by name.
 */
export function CragSectorTopo({
  imageUrl,
  sectorName,
  climbs,
  selectedClimbId,
  onSelect,
  imageMaxHeight,
}: CragSectorTopoProps) {
  // The photo's natural size: every line is stored as fractions of it, so
  // nothing can be drawn before it is known.
  const [naturalSize, setNaturalSize] = useState<{ width: number; height: number } | null>(null);

  // Another sector is another picture, measured again from scratch.
  useEffect(() => {
    setNaturalSize(null);
  }, [imageUrl]);

  const drawn = climbs.filter((climb) => isDrawableLine(climb.line));
  // Read off the whole sector, not only the drawn lines: a route nobody has
  // traced yet still has to say it is the one selected — otherwise picking it
  // looks like the tap was missed.
  const selected = climbs.find((climb) => climb.id === selectedClimbId) ?? null;

  return (
    <div className="relative overflow-hidden rounded-lg border bg-muted">
      <img
        src={imageUrl}
        alt={sectorName}
        className="block w-full object-contain"
        style={imageMaxHeight ? { maxHeight: imageMaxHeight } : undefined}
        onLoad={(event) =>
          setNaturalSize({
            width: event.currentTarget.naturalWidth,
            height: event.currentTarget.naturalHeight,
          })
        }
      />

      {naturalSize && drawn.length > 0 && (
        <svg
          className="absolute inset-0 size-full"
          viewBox={`0 0 ${naturalSize.width} ${naturalSize.height}`}
          preserveAspectRatio="xMidYMid meet"
          data-testid="crag-sector-topo-lines"
        >
          {drawn.map((climb) => {
            if (climb.id === selectedClimbId) return null;

            const d = routeLinePath(climb.line!.points, naturalSize.width, naturalSize.height);
            const width = Math.max(naturalSize.width, naturalSize.height) * lineThickness(climb.line);

            return (
              <g key={climb.id} className="cursor-pointer">
                {/* A hairline is not a target a thumb can be asked to hit, so a
                    fat transparent stroke takes the press for it. The list
                    below is the keyboard's way to the same routes, so this one
                    only has to name itself. */}
                <path
                  d={d}
                  stroke="transparent"
                  strokeWidth={width * 6}
                  strokeLinecap="round"
                  fill="none"
                  role="button"
                  aria-label={climbTitle(climb)}
                  onClick={() => onSelect(climb.id)}
                >
                  <title>{climb.name}</title>
                </path>
                <path
                  d={d}
                  stroke="rgba(0,0,0,0.35)"
                  strokeWidth={width * 2.4}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                  pointerEvents="none"
                />
                <path
                  d={d}
                  stroke="#e4e4e7"
                  strokeWidth={width}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                  opacity={0.5}
                  pointerEvents="none"
                />
              </g>
            );
          })}
        </svg>
      )}

      {/* The lit one, drawn last so it sits over the rest. No sweep: the line is
          the answer to "which one is this", and a route picked from the list
          has to be on the picture the moment it is picked. */}
      {naturalSize && selected && isDrawableLine(selected.line) && (
        <RouteLineOverlay
          line={selected.line}
          naturalWidth={naturalSize.width}
          naturalHeight={naturalSize.height}
          objectFit="contain"
          focusStyle="dim"
          animate={false}
        />
      )}

      {selected && (
        <div className="pointer-events-none absolute bottom-2 left-2 rounded-md bg-black/60 px-2 py-1 text-xs font-medium text-white">
          {selected.name}
          {selected.difficulty ? ` · ${selected.difficulty}` : ''}
        </div>
      )}
    </div>
  );
}
